import { useApolloClient } from '@apollo/client'
import { useCallback, useContext, useEffect, useState } from 'react'
import {
    useLoginMutation,
    useLogoutMutation,
    useMeLazyQuery,
    useMyUpcomingPaymentLazyQuery,
    useUpdateLanguageMutation,
} from 'types/graphql'
import { useRegisterWithConsentMutation, RegisterErrors } from '../containers/onboardingCreateUser/mutations'
import { LogoutReason } from 'utils/authContext'
import { LocalStorageKeys } from 'utils/localStorage'
import { UserContext } from 'v2/context/user'
import { usePerson } from '../context/person'
import { s18n } from '../../i18n'
import { OrganizationContext } from '../context/organization'
import { useRouter } from 'next/router'

export const useAuth = function useAuth() {
    const [logoutMutation] = useLogoutMutation()
    const [loginMutation, { loading: loginLoading, data: loginResponse }] = useLoginMutation()
    const [
        registerWithConsentMutation,
        { loading: registerLoading, data: registerResponse },
    ] = useRegisterWithConsentMutation()
    const [updateLanguage] = useUpdateLanguageMutation()
    const apolloClient = useApolloClient()
    const { person } = usePerson()
    const userContext = useContext(UserContext)
    const { authenticatedUser } = userContext
    const { program, organization } = useContext(OrganizationContext)

    const router = useRouter()

    const [fetchMe, { data: meUserData, called: calledMe, loading: loadingMe, error: errorMe }] = useMeLazyQuery({
        ssr: false,
        fetchPolicy: 'no-cache',
    })

    const [
        fetchUpcomingPayment,
        { data: myUpcomingPaymentData, called: calledUpcomingPayment, loading: loadingUpcomingPayments },
    ] = useMyUpcomingPaymentLazyQuery()

    useEffect(() => {
        if (authenticatedUser) {
            if (
                authenticatedUser.isSuperuser === 0 &&
                authenticatedUser.organization &&
                program &&
                program.id !== authenticatedUser.person.applicationDetails?.[0].program.id
            ) {
                setTimeout(logoutUser, 0)
            }

            if (
                authenticatedUser.isSuperuser > 0 &&
                authenticatedUser.organization &&
                organization &&
                organization.id !== authenticatedUser.organization.id
            ) {
                setTimeout(logoutUser, 0)
            }
        }
    }, [authenticatedUser, organization, program])

    useEffect(() => {
        void fetchAuthenticatedUser()
    }, [])

    const authenticateUser = useCallback(async (email, password) => {
        const loginData = await loginMutation({
            variables: {
                email: email,
                password: password,
            },
        })
        localStorage.setItem(LocalStorageKeys.TOKEN_KEY, loginData.data?.tokenAuth?.token)
        localStorage.setItem(LocalStorageKeys.REFRESH_TOKEN_KEY, loginData.data?.tokenAuth?.refreshToken)

        return fetchAuthenticatedUser()
    }, [])

    const registerUser = useCallback(async (values) => {
        // Format consents for GraphQL (empty array if no consents)
        const consentsArray = values.consents
            ? Object.entries(values.consents).map(([consentTypeId, consentGiven]) => ({
                  consentTypeId,
                  consentGiven: Boolean(consentGiven),
              }))
            : []

        await registerWithConsentMutation({
            variables: {
                email: values.email || person.communicationSetting.email,
                password1: values.password1,
                password2: values.password2,
                securityQuestionQuestion: values.securityQuestion,
                securityQuestionAnswer: values.securityQuestionAnswer,
                consents: consentsArray,
            },
        }).then((response) => {
            if (!response.data.registerWithConsent.errors) {
                return authenticateUser(values.email || person.communicationSetting.email, values.password1).then(
                    () => {
                        void updateLanguage({ variables: { language: s18n.language } })
                    }
                )
            }
        })
    }, [])

    const logoutUser = useCallback(
        async (reason = LogoutReason.General) => {
            try {
                await logoutMutation()
                localStorage.removeItem(LocalStorageKeys.REFRESH_TOKEN_KEY)
                localStorage.removeItem(LocalStorageKeys.TOKEN_KEY)
                localStorage.removeItem(LocalStorageKeys.CARD_BALANCE)
                localStorage.removeItem(LocalStorageKeys.SAW_PREPAID_POPUP)
                userContext.setAuthenticatedUser(null)
                userContext.setTransferMethods([])
                userContext.setUpcomingPayment(null)
            } catch (ignore) {}

            const extraFragment =
                router.pathname.includes('admin') && process.env.NEXT_PUBLIC_ADMIN_ENABLED === 'yes' ? '/admin' : ''

            if (!window.location.pathname.includes('login')) {
                if (window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment) {
                    const url =
                        reason === LogoutReason.Inactivity
                            ? `/v2${
                                  window.PERSON?.applicationDetails[0].program?.urlFragment ||
                                  window.PROGRAM.urlFragment
                              }${extraFragment}/login?inactivity=1`
                            : `/v2${
                                  window.PERSON?.applicationDetails[0].program?.urlFragment ||
                                  window.PROGRAM.urlFragment
                              }${extraFragment}/login`
                    if (window.Intercom) {
                        window.Intercom('shutdown')
                    }

                    void router.replace(url)
                }
            }
        },
        [meUserData, apolloClient, fetchMe]
    )

    useEffect(() => {
        if (calledMe && !loadingMe) {
            s18n.changeLanguage((meUserData?.me?.person?.profile?.language || '').toLowerCase() || s18n.language)
            userContext.setAuthenticatedUser(meUserData?.me || null)

            const transferMethods = meUserData?.me?.paymentProfile.transferMethods?.edges?.map((i) => {
                const clone = JSON.parse(JSON.stringify(i.node))
                delete clone.__typename
                return clone
            })

            userContext.setTransferMethods(transferMethods)
        }
    }, [meUserData, calledMe, errorMe])

    useEffect(() => {
        if (userContext.transferMethods?.length) {
            void fetchUpcomingPayment()
        }
    }, [fetchUpcomingPayment, userContext.transferMethods])

    useEffect(() => {
        if (myUpcomingPaymentData && !loadingUpcomingPayments) {
            userContext.setUpcomingPayment(myUpcomingPaymentData)
        }
    }, [calledUpcomingPayment, myUpcomingPaymentData, userContext.transferMethods])

    const fetchAuthenticatedUser = useCallback(async () => {
        try {
            void fetchMe()
        } catch (ignore) {}
    }, [fetchMe])

    return {
        fetchAuthenticatedUser,
        calledMe,
        errorMe,
        loadingMe,
        registerError: (registerResponse?.registerWithConsent?.errors || {}) as RegisterErrors,
        registerLoading,
        loginLoading,
        loginResponse,
        loginError: loginResponse?.tokenAuth?.errors?.nonFieldErrors[0]?.code,
        registerUser,
        authenticateUser,
        logoutUser,
    }
}
